/* eslint-disable no-shadow */
/* eslint-disable no-extra-boolean-cast */
/* eslint-disable no-plusplus */
import { InterestPointType } from 'models/geozon/enums/interestPoint.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import moment from 'moment';
import React, { useEffect, useState, createContext, useMemo } from 'react';
import {
	fetchGeotileHistory,
	fetchGeotilesLocations,
} from 'models/geotile/controllers/geotile.controller';
import { GeotileHistoryLocationDto } from 'models/geotile/interfaces/geotile.interface';
import { VehicleStatusEnum } from 'models/vehicles/enums/vehicles.enum';
import { fetchGeofences } from 'models/geofence/controllers/geofence.controller';
import { GeofenceStatus } from 'models/geofence/enums/geofence.enum';
import { Geofence } from 'models/geofence/interfaces/geofence.interface';
import { isDriverUser } from 'models/user-management/helpers/driver-check';
import { DeviceLocation } from './GeotileMap.constants';

interface GeotileMapContextModel {
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	loadingHistory: boolean;
	loadingGeofences: boolean;
	gpsLocations: DeviceLocation[];
	gpsHistory: GeotileHistoryLocationDto[];
	geofences: Geofence[];
	showGeofences: boolean;
	setShowGeofences: React.Dispatch<React.SetStateAction<boolean>>;
	selectedMarker?: DeviceLocation;
	setSelectedMarker: React.Dispatch<
		React.SetStateAction<DeviceLocation | undefined>
	>;
	clearContext: () => void;
}

export const geotileMapContext = createContext<GeotileMapContextModel>({
	loading: false,
} as any);

export const GeotileMapContextProvider = ({ children }: { children: any }) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
	const [loadingGeofences, setLoadingGeofences] = useState<boolean>(false);

	const [gpsLocations, setGpsLocations] = useState<DeviceLocation[]>([]);
	const [gpsHistory, setGpsHistory] = useState<GeotileHistoryLocationDto[]>([]);
	const [geofences, setGeofences] = useState<Geofence[]>([]);
	const [showGeofences, setShowGeofences] = useState<boolean>(!isDriverUser());
	const [selectedMarker, setSelectedMarker] = useState<DeviceLocation>();

	const clearContext = () => {
		setGpsLocations([]);
		setGpsHistory([]);
		setSelectedMarker(undefined);
		initializeContext();
	};

	const initializeContext = () => {
		setLoading(true);
		fetchGeotilesLocations()
			.then((res) => {
				setLoading(false);
				setGpsLocations(
					res
						.filter(
							(location) =>
								location.location &&
								location.vehicleData.status === VehicleStatusEnum.active,
						)
						.map((location) => ({
							...location,
							locationType:
								Math.abs(
									moment(location.location.updatedAt).diff(moment(), 'hours'),
								) < 24
									? InterestPointType.signalVehicle
									: InterestPointType.noSignalVehicle,
						})),
				);
			})
			.catch((e) => {
				setLoading(false);
				commonErrorHandler(e);
			});
	};

	const loadGeofences = () => {
		if (isDriverUser() || !showGeofences) {
			setGeofences([]);
			return;
		}

		setLoadingGeofences(true);
		fetchGeofences({ limit: -1, status: GeofenceStatus.ACTIVE })
			.then((res) => {
				const list = Array.isArray(res) ? res : res.data;
				setGeofences(list || []);
				setLoadingGeofences(false);
			})
			.catch((e) => {
				setLoadingGeofences(false);
				commonErrorHandler(e);
			});
	};

	useEffect(() => {
		initializeContext();
	}, []);

	useEffect(() => {
		loadGeofences();
	}, [showGeofences]);

	useEffect(() => {
		setGpsHistory([]);
		if (selectedMarker) {
			fetchGpsHistory();
		}
	}, [selectedMarker]);

	const fetchGpsHistory = () => {
		if (selectedMarker?._id) {
			setLoadingHistory(true);
			const dias = moment().diff(
				moment(selectedMarker.vehicleData.createdAt),
				'days',
			);
			fetchGeotileHistory(selectedMarker._id, Math.min(dias, 90))
				.then((data) => {
					setLoadingHistory(false);
					setGpsHistory(data.locations);
				})
				.catch((error) => {
					setLoadingHistory(false);
					commonErrorHandler(error);
				});
		} else {
			commonErrorHandler('No hay informacion disponible del dispositivo');
		}
	};

	const contextValue: GeotileMapContextModel = useMemo(
		() => ({
			loading,
			setLoading,
			loadingHistory,
			loadingGeofences,
			gpsLocations,
			gpsHistory,
			geofences,
			showGeofences,
			setShowGeofences,
			selectedMarker,
			setSelectedMarker,
			clearContext,
		}),
		[
			loading,
			loadingHistory,
			loadingGeofences,
			gpsLocations,
			gpsHistory,
			geofences,
			showGeofences,
			selectedMarker,
		],
	);
	return (
		<geotileMapContext.Provider value={contextValue}>
			{children}
		</geotileMapContext.Provider>
	);
};
